import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ExclamationTriangleIcon, 
  ShieldExclamationIcon,
  ArrowLeftIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { getRoleDisplayName } from '../utils/permissions';

const AccessDenied = ({ 
  requiredPermission = null, 
  userRole = null, 
  resource = null,
  showBackButton = true 
}) => {
  const getMessage = () => {
    if (requiredPermission) {
      return `You need the "${requiredPermission}" permission to access this page.`;
    }
    return "You don't have permission to access this page.";
  };

  const getSubMessage = () => {
    if (userRole) {
      return `Your current role is "${getRoleDisplayName(userRole)}". Please contact an administrator if you need access to this feature.`;
    }
    return "Please contact an administrator if you need access to this feature.";
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <ShieldExclamationIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Access Denied
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" aria-hidden="true" />
            </div>
            
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Permission Required
            </h3>
            
            <p className="text-sm text-gray-600 mb-4">
              {getMessage()}
            </p>
            
            <p className="text-xs text-gray-500 mb-6">
              {getSubMessage()}
            </p>

            <div className="space-y-3">
              {showBackButton && (
                <button
                  onClick={() => window.history.back()}
                  className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Go Back
                </button>
              )}
              
              <Link
                to="/"
                className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <HomeIcon className="h-4 w-4 mr-2" />
                Go to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Additional help section */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">
            Need Help?
          </h4>
          <p className="text-xs text-blue-700">
            If you believe you should have access to this feature, please contact your system administrator. 
            They can review your permissions and grant access if appropriate.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccessDenied; 